import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Play,
  FileText,
  Key,
  Webhook,
  BookOpen,
  FlaskConical,
  Keyboard,
} from 'lucide-react';
import {
  ExplorerTab,
  LogsTab,
  QuickstartTab,
  ApiKeysTab,
  WebhooksTab,
} from '../components/dev-workbench';
import { useKeyboardShortcuts, getModifierKey } from '../components/dev-workbench/shared/useKeyboardShortcuts';
import KeyboardShortcutsPanel from '../components/dev-workbench/shared/KeyboardShortcutsPanel';
import '../styles/design-tokens.css';
import '../styles/workbench.css';

const TABS = [
  { id: 'explorer', label: 'Explorer', icon: Play },
  { id: 'logs', label: 'Logs', icon: FileText },
  { id: 'quickstart', label: 'Quickstart', icon: BookOpen },
  { id: 'keys', label: 'API Keys', icon: Key },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
];

export default function DevWorkbenchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'explorer');
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Update URL when tab changes
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabId);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Toggle shortcuts panel
  const handleToggleHelp = useCallback(() => {
    setShowShortcuts(prev => !prev);
  }, []);

  // Toggle magic values panel (dispatch event to ExplorerTab)
  const handleToggleMagicValues = useCallback(() => {
    window.dispatchEvent(new CustomEvent('toggleMagicValues'));
  }, []);

  // Handle run request (dispatch to ExplorerTab)
  const handleRun = useCallback(() => {
    // Dispatch custom event for ExplorerTab to listen to
    window.dispatchEvent(new CustomEvent('runWorkbenchRequest'));
  }, []);

  // Setup keyboard shortcuts
  useKeyboardShortcuts({
    onRun: handleRun,
    onTabChange: handleTabChange,
    onToggleHelp: handleToggleHelp,
    onToggleMagicValues: handleToggleMagicValues,
    enabled: true,
  });

  const modKey = getModifierKey();

  return (
    <div className="workbench-page">
      <div className="workbench-header">
        <div className="workbench-header-icon">
          <FlaskConical size={24} aria-hidden="true" />
        </div>
        <div>
          <h1 className="workbench-title">Dev Workbench</h1>
          <p className="workbench-subtitle">
            Test APIs, view logs, and manage your integration
          </p>
        </div>
      </div>

      <div className="workbench-tabs" role="tablist" aria-label="Dev Workbench tabs">
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            className={`workbench-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            aria-label={`Switch to ${tab.label} tab (${modKey}+${index + 1})`}
          >
            <tab.icon size={16} aria-hidden="true" />
            {tab.label}
          </button>
        ))}
        <button
          className="workbench-tab workbench-tab-help"
          onClick={handleToggleHelp}
          aria-label="Show keyboard shortcuts (?)"
          title="Keyboard shortcuts (?)"
        >
          <Keyboard size={16} aria-hidden="true" />
        </button>
      </div>

      <div
        className="workbench-content"
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === 'explorer' && <ExplorerTab />}
        {activeTab === 'logs' && <LogsTab />}
        {activeTab === 'quickstart' && <QuickstartTab />}
        {activeTab === 'keys' && <ApiKeysTab />}
        {activeTab === 'webhooks' && <WebhooksTab />}
      </div>

      {/* Keyboard Shortcuts Panel */}
      {showShortcuts && (
        <KeyboardShortcutsPanel onClose={() => setShowShortcuts(false)} />
      )}
    </div>
  );
}
